import { registerBlock } from "../registry";
import { makeId } from "../helpers";
import type { ContentTag } from "../types";

// ─── Article 1: Transitioning from ART to pregnancy ─────────────────

registerBlock({
  id: makeId("new-article-transition-art-to-pregnancy"),
  kind: "article",
  tags: ["post-cycle", "practical", "emotional"] as ContentTag[],
  title: "From ART to pregnancy: what's next",
  subtitle:
    "The first weeks after a positive result — monitoring, transitioning care, and taking it in.",
  readTimeMinutes: 6,
  body: {
    introduction:
      "The transition from Assisted Reproductive Technology to a confirmed pregnancy is a significant milestone. After an intense journey of procedures and waiting, a positive result can be both exhilarating and overwhelming. Here is a calm overview of the next steps.",
    quickSummary: [
      {
        title: "Confirmatory tests come first.",
        description:
          "Beta hCG bloodwork and an early ultrasound (around 6–8 weeks) confirm the pregnancy and check for heartbeat.",
      },
      {
        title: "Care transfers to an OB.",
        description:
          "Once the pregnancy is stable, your fertility team hands off to an obstetrician or midwife.",
      },
      {
        title: "Some ART meds continue for a while.",
        description:
          "Progesterone or estrogen support is usually tapered under medical supervision, not stopped at once.",
      },
    ],
    sections: [
      {
        title: "Monitoring the early pregnancy",
        subtitle: "What the clinic checks in the first weeks",
        content:
          "Even after a positive home pregnancy test, your fertility clinic will usually confirm with a blood test (beta hCG). Multiple draws are common to check that hormone levels are rising as expected. An early ultrasound is typically scheduled between 6 and 8 weeks to look for a heartbeat and confirm the number of embryos that implanted.",
        bulletPoints: [
          "Beta hCG bloodwork confirms the pregnancy and tracks its rise.",
          "Serial draws (every few days) are standard for the first weeks.",
          "An early ultrasound around 6–8 weeks looks for a heartbeat and count.",
        ],
      },
      {
        title: "Transitioning to obstetric care",
        subtitle: "From fertility team to OB",
        content:
          "Once the pregnancy is stable — usually after a heartbeat is detected — your care shifts from a fertility specialist to an obstetrician or midwife. This transition can be bittersweet, especially if you've formed close connections with your fertility team. Ask your clinic to transfer all relevant records so your new provider has the full picture.",
        bulletPoints: [
          "The handoff usually happens after 8–10 weeks.",
          "Ask for full records transfer, including protocol and lab results.",
          "It's normal to feel a mix of relief and a small loss at leaving the fertility team.",
        ],
      },
      {
        title: "Adjusting medications",
        subtitle: "Never stop on your own",
        content:
          "Some medications used during ART (progesterone, estrogen) are continued into early pregnancy. Your doctor will guide when and how to taper safely. Do not stop any medication on your own, even if you feel fine — the timing matters.",
        bulletPoints: [
          "Progesterone support is often continued until 10–12 weeks.",
          "Tapering happens on the clinic's timeline, not yours.",
          "Call the clinic before stopping anything, even if you feel great.",
        ],
      },
      {
        title: "Physical changes and symptoms",
        subtitle: "What to expect in the first weeks",
        content:
          "Early pregnancy brings fatigue, nausea, breast tenderness, and mood shifts. Intensity varies widely. Your body has been through a lot with ART, and it will continue to change as the pregnancy progresses. Regular prenatal visits will monitor both your health and the baby's.",
        bulletPoints: [
          "Fatigue and mild nausea are common and rarely a cause for alarm.",
          "Bleeding, severe pain, or fainting should trigger an immediate call.",
          "Every early pregnancy feels different — comparison is not useful.",
        ],
      },
      {
        title: "Emotional well-being",
        subtitle: "It's normal to feel a lot at once",
        content:
          "Mixed emotions are the norm after a positive ART result: relief, joy, anxiety, disbelief. ART is a long journey, and the transition to pregnancy carries its own emotional weight. Some people find continuing therapy or support groups helpful.",
        bulletPoints: [
          "It's OK to feel scared even when the news is good.",
          "Prenatal support groups can help bridge the ART community with pregnancy.",
          "Therapy that helped during ART often helps during early pregnancy.",
        ],
      },
      {
        title: "Preparing for the next chapter",
        subtitle: "Small, unhurried steps",
        content:
          "Nothing needs to be decided today. Prenatal vitamins, regular checkups, and eventually thinking about where to give birth are part of the coming months. Take it at your own pace.",
        bulletPoints: [
          "Prenatal vitamins with folic acid are recommended early.",
          "Birth location decisions can wait — most people decide by 20 weeks.",
          "Childbirth classes and provider tours come later, not now.",
        ],
      },
    ],
  },
});

// ─── Article 2: Dealing with ART failure ────────────────────────────

registerBlock({
  id: makeId("new-article-dealing-with-art-failure"),
  kind: "article",
  tags: ["post-cycle", "emotional", "practical"] as ContentTag[],
  title: "When ART doesn't work: grief, recovery, next steps",
  subtitle:
    "How to process a failed cycle without blaming yourself, and how to think about what comes after.",
  readTimeMinutes: 7,
  body: {
    introduction:
      "The failure of an ART procedure can trigger deep emotional responses. Treatments come with high hopes, and the ending — whichever form it takes — is genuinely hard. This article covers both the emotional and the medical parts of moving through it.",
    quickSummary: [
      {
        title: "Grief is normal.",
        description:
          "Mourning what was hoped for is part of the process. Give yourself the time to feel it.",
      },
      {
        title: "It's not your fault.",
        description:
          "ART outcomes depend on many factors beyond individual control. Self-blame is a natural reflex but not an accurate one.",
      },
      {
        title: "Support matters.",
        description:
          "Counseling, support groups, and honest conversations with your care team all help.",
      },
    ],
    sections: [
      {
        title: "Understanding what happened",
        subtitle: "The reality of ART outcomes",
        content:
          "ART success rates vary widely depending on biology, age, medical conditions, genetics, and factors we don't fully understand. In IVF, common contributors to a failed cycle include ovarian reserve, embryo development quality, and endometrial receptivity. In IUI, sperm quality and timing play major roles. Not every failed cycle points to a single cause.",
        bulletPoints: [
          "Success rates decline with age but individual outcomes vary widely.",
          "Sometimes there is no identifiable single reason.",
          "Reviewing the cycle with your care team can surface next-step ideas.",
        ],
      },
      {
        title: "Grief and coping",
        subtitle: "What tends to help",
        content:
          "The emotions can range from sadness and grief to feelings of inadequacy or guilt. These are natural reactions, not signs of weakness. What helps varies from person to person, but a few strategies come up repeatedly.",
        bulletPoints: [
          "Grieve on your own timeline. There is no rushing this.",
          "Avoid self-blame — ART outcomes are not a measure of personal worth.",
          "Consider counseling with someone who understands fertility loss.",
          "Couples counseling can help partners process the loss together.",
          "Peer support groups (in-person or online) reduce isolation.",
        ],
      },
      {
        title: "Living beyond fertility treatment",
        subtitle: "Reconnecting with the rest of your life",
        content:
          "The journey through ART can consume everything else. Reconnecting with hobbies, exercise, and social connection matters — not because you should distract yourself from grief, but because you deserve moments that aren't defined by treatment.",
        bulletPoints: [
          "Small breaks from fertility-focused activities are restorative.",
          "Physical movement (walking, gentle yoga) supports emotional recovery.",
          "It's OK to take a formal break from treatment to reassess.",
        ],
      },
      {
        title: "Reviewing next steps with your care team",
        subtitle: "What to ask, and when",
        content:
          "After an unsuccessful ART cycle, a follow-up appointment with your fertility team is standard. This is a chance to review the cycle data (medications, response, embryology, if applicable) and discuss possible protocol changes or additional testing. There is no rush to schedule this — take the time you need first.",
        bulletPoints: [
          "Follow-up appointments usually happen 4–8 weeks after a failed cycle.",
          "Ask about protocol adjustments, additional testing, or alternative approaches.",
          "A second opinion is a reasonable option, not a betrayal of your team.",
          "Financial planning matters — additional cycles have real cost.",
        ],
      },
      {
        title: "Exploring alternative paths",
        subtitle: "When the current path is not the only path",
        content:
          "Depending on your situation, alternative options may come up in the follow-up conversation: switching ART methods, considering donor eggs or sperm, or exploring adoption or fostering. These are not always the immediate next step, but they are worth knowing about.",
        bulletPoints: [
          "Switching modalities (IVF → FET with a stored embryo, IUI → IVF).",
          "Donor gametes when biology suggests they'd help.",
          "Adoption or foster care as parallel paths to parenthood.",
          "Choosing to stop treatment is also a valid decision.",
        ],
      },
      {
        title: "Key takeaways",
        subtitle: "Small anchors to hold onto",
        content:
          "The path through unsuccessful ART is not linear. Grief, resilience, questions, and hope can coexist. Community support — family, friends, counseling, groups — makes the process less isolating. Resilience is not the absence of grief; it's the capacity to keep moving, at your own pace.",
      },
    ],
  },
});

// ─── Article 3: Emotional impact of infertility ─────────────────────

registerBlock({
  id: makeId("new-article-emotional-impact-infertility"),
  kind: "article",
  tags: ["emotional", "practical"] as ContentTag[],
  title: "The emotional weight of infertility: you're not alone",
  subtitle:
    "Anxiety, grief, self-blame — the very human responses to a very hard situation.",
  readTimeMinutes: 8,
  body: {
    introduction:
      "Infertility is not just a medical problem. It touches mood, relationships, body image, identity, and plans for the future. If you feel overwhelmed, numb, angry, or unlike yourself — you are not alone. Research consistently shows that people facing infertility experience anxiety and depression symptoms at rates comparable to those living with other serious medical conditions.",
    quickSummary: [
      {
        title: "It's not you being too sensitive.",
        description:
          "The emotional burden of infertility is real, measured, and comparable to other serious illnesses.",
      },
      {
        title: "Common feelings, shared widely.",
        description:
          "Grief, anxiety, shame, envy, relationship strain — many people going through this experience the same things.",
      },
      {
        title: "Support helps.",
        description:
          "Naming feelings, protecting bandwidth, finding people who get it, and considering professional help all make a real difference.",
      },
    ],
    sections: [
      {
        title: "Why infertility hits so hard",
        subtitle: "What makes it uniquely difficult",
        content:
          "A few things make infertility particularly tough emotionally: it strikes at something deeply personal, it's full of uncertainty and waiting, it can be invisible and isolating, and the treatment itself is intense.",
        bulletPoints: [
          "It shakes identity: the ability to have children is often tied to a life story imagined since childhood.",
          "Uncertainty and repeated cycles create a hope-crash pattern that is exhausting.",
          "From the outside you may look fine — many people don't know what to say, or say the wrong thing.",
          "Treatment itself is a full-time mental job on top of everything else.",
        ],
      },
      {
        title: "Common feelings that many others share",
        subtitle: "You are not alone in these",
        content:
          "Everyone's experience is different, but some themes come up again and again. Recognizing them can loosen their grip.",
        bulletPoints: [
          "Grief and loss — not of a baby, but of the simple path you hoped for.",
          "Anxiety and constant mental noise — What if this never works? Did I miss my window?",
          "Shame and self-blame — thoughts like 'my body is failing' that you'd never apply to someone else.",
          "Anger and envy — the stab of pain at a pregnancy announcement is not a failure of character.",
          "Relationship strain — partners often cope differently, and sex can start to feel like a medical task.",
        ],
      },
      {
        title: "It's not just in your head",
        subtitle: "Stress and fertility, the honest version",
        content:
          "People sometimes worry that admitting how low they feel will trigger unhelpful advice like 'just relax' or the implication that stress caused their infertility. The evidence is more nuanced: stress and mood do not cause most structural or hormonal fertility problems. But ongoing high stress makes everything harder — sleeping, eating, staying connected to your partner. Mental health is not a luxury here; it's part of your overall health during a difficult medical situation.",
      },
      {
        title: "Small ways to cope",
        subtitle: "You don't have to fix everything at once",
        content:
          "Think in small, doable supports. None of these are cures. All of them help.",
        bulletPoints: [
          "Put words on what you're feeling — even a single sentence in a journal helps.",
          "Protect your mental bandwidth — mute pregnancy-related content, say no to hard events.",
          "Find people who get it — support groups, online communities, one trusted friend.",
          "Consider professional support — a therapist who understands fertility and medical trauma.",
        ],
      },
      {
        title: "When to reach out urgently",
        subtitle: "Signs that need more support than a friend",
        content:
          "Please contact emergency services, a crisis line, or your doctor quickly if you notice any of the following. These are not signs of weakness — they mean your nervous system is overwhelmed and needs more support.",
        bulletPoints: [
          "Persistent thoughts that everyone would be better off without you.",
          "Self-harm ideas or behaviors.",
          "Inability to get out of bed, work, or handle basic tasks.",
          "Panic attacks that don't ease, or crushing anxiety every day.",
          "Substance use spiraling as a coping mechanism.",
        ],
      },
      {
        title: "You are not alone in this",
        subtitle: "Some anchors to keep",
        content:
          "Infertility can shrink the world to clinic waiting rooms, blood draws, and calendar counts. Even when everyone else's life seems to be moving forward while yours feels stuck, you are not failing and you are not alone. What you're feeling is real, valid, and shared by many others walking a similar path. Needing help doesn't mean you're weak. It means you've been strong for too long without enough support.",
      },
    ],
  },
});

// ─── Article 4: Daily tips during ovarian stimulation ───────────────

registerBlock({
  id: makeId("new-article-stim-daily-tips"),
  kind: "article",
  tags: ["during-cycle", "practical", "medications"] as ContentTag[],
  title: "Daily habits during stimulation",
  subtitle:
    "Small routines that make IVF or egg freezing feel more manageable.",
  readTimeMinutes: 7,
  body: {
    introduction:
      "Ovarian stimulation is the part of IVF (or egg freezing) where you take fertility medications for about 8 to 13 days so multiple follicles can grow at once. Monitoring visits check how your body responds and the clinic adjusts the plan accordingly. It's time-sensitive, physical, and emotional all at once — this article offers small routines to keep it manageable.",
    quickSummary: [
      {
        title: "Timing is everything.",
        description:
          "Stimulation is one of those rare times when being 30 minutes late actually matters. Pick a time, set two alarms, protect it.",
      },
      {
        title: "Symptoms are information, not prediction.",
        description:
          "Bloating, mood swings, fatigue — all reflect hormones and ovarian growth. They don't tell you whether the cycle will work.",
      },
      {
        title: "Know the signs that mean 'call today'.",
        description:
          "Severe pain, rapid abdominal enlargement, shortness of breath — these deserve a same-day call to the clinic.",
      },
    ],
    sections: [
      {
        title: "Make timing your top priority",
        subtitle: "Injections don't wait",
        content:
          "Stimulation medications have narrow windows. Pick a daily time you can realistically protect, on weekdays and weekends. Set two alarms — one 15 minutes before, one at the exact time. If you travel or cross time zones, message your clinic first so you don't have to guess. If you miss or delay a dose, don't panic and don't double up on your own — call the clinic and follow their instructions.",
        bulletPoints: [
          "Pick a time you can protect every day.",
          "Two alarms: one 15 min before, one at the exact time.",
          "Cross-time-zone travel: message the clinic first.",
          "Missed dose: call, don't guess or double up.",
        ],
      },
      {
        title: "Build a 5-minute injection routine",
        subtitle: "Decision fatigue is the real enemy",
        content:
          "Most people don't struggle with the injection itself — they struggle with the decisions around it. A calm setup helps: one dedicated spot at home, all supplies in one box, and a simple checklist you follow the same way every time. The first two days are usually the hardest. After that, it becomes routine.",
      },
      {
        title: "Plan your mornings like monitoring might happen",
        subtitle: "Because it will, often",
        content:
          "Monitoring visits can shift your workday quickly, especially as the clinic adjusts your schedule based on results. Keep mornings flexible if possible — early meetings are the hardest to protect during stim. Save clinic parking and contact info in your favorites. Bring a snack if your clinic combines bloodwork and ultrasound; you may be there longer than expected.",
      },
      {
        title: "Expect bloating and pelvic heaviness",
        subtitle: "Adjust your life accordingly",
        content:
          "As follicles grow, ovaries enlarge. Feeling full, bloated, or crampy is completely normal, especially in the last few days before retrieval. Loose pants and breathable underwear help. Smaller, more frequent meals are easier than big ones. Plan quieter evenings toward the end of stimulation — that's when heaviness usually peaks.",
        bulletPoints: [
          "Loose clothing helps more than you'd think.",
          "Smaller meals, more often, if you're uncomfortable.",
          "Constipation is common — ask your clinic before self-treating.",
          "Quieter evenings in the last few days help energy last.",
        ],
      },
      {
        title: "Keep movement gentle",
        subtitle: "Low-impact only once ovaries enlarge",
        content:
          "Most clinics recommend avoiding high-impact exercise, heavy lifting, and twisting workouts once ovaries are enlarged — discomfort and the (rare) risk of ovarian torsion both go up. Light movement is usually fine. Walking, gentle yoga without deep twists, and light stretching are good defaults.",
      },
      {
        title: "Hydration is a real comfort tool",
        subtitle: "Not a magic number, just steady",
        content:
          "There's no perfect number of ounces. But dehydration makes bloating, headaches, and fatigue feel worse. Start the day with water. Add a glass after monitoring visits. If you feel puffy or crampy, steady hydration usually helps more than chugging all at once. If your clinic mentions OHSS risk, follow their specific guidance.",
      },
      {
        title: "Symptoms are not a scoreboard",
        subtitle: "Feeling great or feeling rough are both normal",
        content:
          "During stimulation, symptoms reflect hormones and ovarian growth — not whether the cycle will work. Bloating, breast tenderness, mood swings, and fatigue are all common. Some people feel great; some feel rough. Both are normal. Symptoms are information about your body, not a prediction of your result.",
      },
      {
        title: "Protect your brain, not just your body",
        subtitle: "Small boundaries help a lot",
        content:
          "Stimulation can take over your mental bandwidth. Pick one trustworthy info source (your clinic portal, this app, or a single patient education site). Limit forum scrolling when you notice it increasing anxiety. Tell one or two people exactly how to support you — rides, meals, distraction, check-ins.",
      },
      {
        title: "Know the signs that mean 'call today'",
        subtitle: "Most side effects are manageable — these are not",
        content:
          "Most symptoms during stimulation are manageable, but your clinic wants to hear from you promptly if any of these appear:",
        bulletPoints: [
          "Severe or worsening abdominal pain.",
          "Rapid weight gain or rapidly increasing abdominal size.",
          "Shortness of breath.",
          "Persistent vomiting or inability to keep fluids down.",
          "Feeling faint or unusually unwell.",
        ],
      },
      {
        title: "The mental shift that helps",
        subtitle: "Stim is a sprint, not a marathon",
        content:
          "Try to treat stimulation as a short, time-limited sprint. You're not supposed to do everything perfectly. Show up, follow timing, report symptoms honestly, and let the clinic do the adjusting. You're doing enough.",
      },
    ],
  },
});
